/**
 * Tile System Configuration
 * Tile pyramid with multiple zoom levels (6 LOD levels: 0-5)
 *
 * Zoom level convention:
 * - 0 = highest detail (drawing level, 1 world unit = 1 pixel)
 * - 5 = lowest detail (overview, most zoomed out LOD)
 *
 * Higher zoom numbers = less detail (more zoomed out LOD)
 */

/**
 * Size of each tile in pixels (web standard)
 */
export const TILE_SIZE = 256;

/**
 * Minimum zoom level (highest detail for drawing)
 * At MIN_ZOOM (0), 1 world unit = 1 pixel
 */
export const MIN_ZOOM = 0;

/**
 * Maximum zoom level (lowest detail overview)
 * This is the most zoomed out LOD level
 */
export const MAX_ZOOM = 5;

/**
 * Detail zoom level - the level at which drawing occurs
 * This is always MIN_ZOOM (0) which has the highest detail
 */
export const DETAIL_ZOOM = MIN_ZOOM;

/**
 * Total world size in world units
 * TILE_SIZE * 2^MAX_ZOOM = 256 * 32 = 8192
 * This gives us a reasonable world size with 6 LOD levels (0-5)
 */
export const WORLD_SIZE = TILE_SIZE * Math.pow(2, MAX_ZOOM);

/**
 * Tile coordinate interface
 */
export interface TileCoord {
  z: number;
  x: number;
  y: number;
}

/**
 * Cached tile interface
 */
export interface CachedTile {
  canvas: HTMLCanvasElement;
  dirty: boolean;
  lastAccess: number;
  hasContent: boolean;
  z: number;
  x: number;
  y: number;
  /** Opacity for fade in/out animation (0 to 1) */
  opacity: number;
  /** Timestamp when tile started loading/appearing */
  appearTime: number;
}

/**
 * Calculates the world size covered by a single tile at a given zoom level
 * With inverted zoom: z=0 is detail (small tiles), z=5 is overview (large tiles)
 * @param zoom - Zoom level (0 to MAX_ZOOM)
 * @returns Size in world units that one tile covers
 */
export function getTileWorldSize(zoom: number): number {
  // z=0: 256 world units (detail)
  // z=5: 8192 world units (overview)
  return TILE_SIZE * Math.pow(2, zoom);
}

/**
 * Converts world coordinates to tile coordinates at a given zoom level
 * Supports infinite world (negative coordinates)
 * @param worldX - X position in world units
 * @param worldY - Y position in world units
 * @param zoom - Zoom level
 * @returns Tile coordinates {x, y}
 */
export function worldToTile(worldX: number, worldY: number, zoom: number): { x: number; y: number } {
  const tileWorldSize = getTileWorldSize(zoom);

  // Origin at center of world (WORLD_SIZE/2), supports negative coordinates
  // At z=0, we have 32x32 tiles covering the world
  // At z=5, we have 1x1 tile covering the world
  const x = Math.floor((worldX + WORLD_SIZE / 2) / tileWorldSize);
  const y = Math.floor((worldY + WORLD_SIZE / 2) / tileWorldSize);

  return { x, y };
}

/**
 * Converts tile coordinates to world coordinates (top-left corner of tile)
 * @param tileX - Tile X index
 * @param tileY - Tile Y index
 * @param zoom - Zoom level
 * @returns World coordinates {x, y} of tile's top-left corner
 */
export function tileToWorld(tileX: number, tileY: number, zoom: number): { x: number; y: number } {
  const tileWorldSize = getTileWorldSize(zoom);

  return {
    x: tileX * tileWorldSize - WORLD_SIZE / 2,
    y: tileY * tileWorldSize - WORLD_SIZE / 2
  };
}

/**
 * Gets the pixel position within a tile for a world coordinate
 * @param worldX - X position in world units
 * @param worldY - Y position in world units
 * @param zoom - Zoom level
 * @returns Pixel coordinates within the tile {px, py}
 */
export function worldToTilePixel(worldX: number, worldY: number, zoom: number): { px: number; py: number } {
  const tile = worldToTile(worldX, worldY, zoom);
  const tileWorld = tileToWorld(tile.x, tile.y, zoom);
  const tileWorldSize = getTileWorldSize(zoom);
  const scale = TILE_SIZE / tileWorldSize;

  return {
    px: (worldX - tileWorld.x) * scale,
    py: (worldY - tileWorld.y) * scale
  };
}

/**
 * Generates a unique key for a tile
 * @param z - Zoom level
 * @param x - Tile X index
 * @param y - Tile Y index
 * @returns String key in format "z/x/y"
 */
export function getTileKey(z: number, x: number, y: number): string {
  return `${z}/${x}/${y}`;
}

/**
 * Parses a tile key back to coordinates
 * @param key - Tile key in format "z/x/y"
 * @returns TileCoord object
 */
export function parseTileKey(key: string): TileCoord {
  const parts = key.split('/').map(Number);
  return { z: parts[0], x: parts[1], y: parts[2] };
}

/**
 * Gets the parent tile coordinates (one zoom level up = less detail)
 * Parent tiles are at higher zoom numbers (lower detail)
 * @param z - Current zoom level
 * @param x - Tile X index
 * @param y - Tile Y index
 * @returns Parent tile coordinates, or null if at MAX_ZOOM (lowest detail)
 */
export function getParentTile(z: number, x: number, y: number): TileCoord | null {
  if (z >= MAX_ZOOM) return null;

  return {
    z: z + 1,
    x: Math.floor(x / 2),
    y: Math.floor(y / 2)
  };
}

/**
 * Gets the four child tile coordinates (one zoom level down = more detail)
 * Child tiles are at lower zoom numbers (higher detail)
 * @param z - Current zoom level
 * @param x - Tile X index
 * @param y - Tile Y index
 * @returns Array of 4 child tile coordinates, or null if at MIN_ZOOM (highest detail)
 */
export function getChildTiles(z: number, x: number, y: number): TileCoord[] | null {
  if (z <= MIN_ZOOM) return null;

  const childZ = z - 1;
  const baseX = x * 2;
  const baseY = y * 2;

  return [
    { z: childZ, x: baseX,     y: baseY },     // Top-left
    { z: childZ, x: baseX + 1, y: baseY },     // Top-right
    { z: childZ, x: baseX,     y: baseY + 1 }, // Bottom-left
    { z: childZ, x: baseX + 1, y: baseY + 1 }  // Bottom-right
  ];
}

/**
 * Gets all ancestor tiles from current zoom to MAX_ZOOM (lowest detail)
 * Ancestors are at higher zoom numbers (lower detail)
 * @param z - Current zoom level
 * @param x - Tile X index
 * @param y - Tile Y index
 * @returns Array of ancestor tile coordinates (excluding current tile)
 */
export function getAncestorTiles(z: number, x: number, y: number): TileCoord[] {
  const ancestors: TileCoord[] = [];
  let currentZ = z;
  let currentX = x;
  let currentY = y;

  while (currentZ < MAX_ZOOM) {
    currentX = Math.floor(currentX / 2);
    currentY = Math.floor(currentY / 2);
    currentZ++;
    ancestors.push({ z: currentZ, x: currentX, y: currentY });
  }

  return ancestors;
}

/**
 * Calculates which quadrant a child tile occupies in its parent
 * @param childX - Child tile X index
 * @param childY - Child tile Y index
 * @returns Quadrant info { quadX: 0|1, quadY: 0|1, destX, destY }
 */
export function getChildQuadrant(childX: number, childY: number): {
  quadX: number;
  quadY: number;
  destX: number;
  destY: number
} {
  const quadX = childX % 2;
  const quadY = childY % 2;

  return {
    quadX,
    quadY,
    destX: quadX * (TILE_SIZE / 2),
    destY: quadY * (TILE_SIZE / 2)
  };
}

/**
 * Calculates the optimal tile zoom level for a given viewport zoom
 * Target: tiles displayed at approximately 256 pixels on screen
 *
 * With the new system:
 * - zoom 0 = highest detail (tileWorldSize = 256)
 * - zoom 5 = lowest detail (tileWorldSize = 8192)
 *
 * @param viewportZoom - Current viewport zoom (screen pixels per world unit)
 * @returns Optimal tile zoom level (0-5)
 */
export function getOptimalZoom(viewportZoom: number): number {
  // tileWorldSize(z) = WORLD_SIZE / 2^z = 8192 / 2^z
  // At z=0: tileWorldSize = 8192 / 1 = 8192? No wait...
  //
  // Actually with inverted system:
  // - z=0 (detail): tileWorldSize should be small (256)
  // - z=5 (overview): tileWorldSize should be large (8192)
  //
  // So: tileWorldSize(z) = TILE_SIZE * 2^z = 256 * 2^z
  // At z=0: 256, at z=5: 8192
  //
  // tileScreenSize = tileWorldSize * viewportZoom = 256 * 2^z * viewportZoom
  // We want tileScreenSize ~= 256 (tiles ~256px on screen)
  // 256 = 256 * 2^z * viewportZoom
  // 1 = 2^z * viewportZoom
  // 2^z = 1 / viewportZoom
  // z = -log2(viewportZoom)

  if (viewportZoom <= 0) return MAX_ZOOM;

  const z = -Math.log2(viewportZoom);
  return Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, Math.round(z)));
}

/**
 * Gets all tiles visible in a viewport at a specific zoom level
 * @param viewportX - Viewport center X in world units
 * @param viewportY - Viewport center Y in world units
 * @param canvasWidth - Canvas width in pixels
 * @param canvasHeight - Canvas height in pixels
 * @param viewportZoom - Viewport zoom (screen pixels per world unit)
 * @param tileZoom - Tile zoom level to query
 * @returns Array of visible tile coordinates
 */
export function getVisibleTiles(
  viewportX: number,
  viewportY: number,
  canvasWidth: number,
  canvasHeight: number,
  viewportZoom: number,
  tileZoom: number
): TileCoord[] {
  // Calculate viewport bounds in world units
  const halfWidth = canvasWidth / (2 * viewportZoom);
  const halfHeight = canvasHeight / (2 * viewportZoom);

  const worldMinX = viewportX - halfWidth;
  const worldMaxX = viewportX + halfWidth;
  const worldMinY = viewportY - halfHeight;
  const worldMaxY = viewportY + halfHeight;

  // Convert to tile coordinates
  const minTile = worldToTile(worldMinX, worldMinY, tileZoom);
  const maxTile = worldToTile(worldMaxX, worldMaxY, tileZoom);

  // Limit the number of tiles to avoid performance issues at extreme zoom levels
  // Max 32 tiles per axis = 1,024 tiles max total
  const MAX_TILES_PER_AXIS = 32;
  const tilesX = maxTile.x - minTile.x + 1;
  const tilesY = maxTile.y - minTile.y + 1;

  let startX = minTile.x;
  let endX = maxTile.x;
  let startY = minTile.y;
  let endY = maxTile.y;

  // If too many tiles, center the visible area around viewport center
  if (tilesX > MAX_TILES_PER_AXIS) {
    const centerTile = worldToTile(viewportX, viewportY, tileZoom);
    const halfTiles = Math.floor(MAX_TILES_PER_AXIS / 2);
    startX = centerTile.x - halfTiles;
    endX = centerTile.x + halfTiles;
  }

  if (tilesY > MAX_TILES_PER_AXIS) {
    const centerTile = worldToTile(viewportX, viewportY, tileZoom);
    const halfTiles = Math.floor(MAX_TILES_PER_AXIS / 2);
    startY = centerTile.y - halfTiles;
    endY = centerTile.y + halfTiles;
  }

  // Generate list of visible tiles
  const tiles: TileCoord[] = [];

  for (let x = startX; x <= endX; x++) {
    for (let y = startY; y <= endY; y++) {
      tiles.push({ z: tileZoom, x, y });
    }
  }

  return tiles;
}

/**
 * Sorts tiles by distance from viewport center (spiral/center-first order)
 * @param tiles - Array of tile coordinates
 * @param viewportX - Viewport center X in world units
 * @param viewportY - Viewport center Y in world units
 * @returns Sorted array (closest to center first)
 */
export function sortTilesByDistance(
  tiles: TileCoord[],
  viewportX: number,
  viewportY: number
): TileCoord[] {
  // Pre-calculate distances for better sort performance
  // Avoids repeated tileToWorld/getTileWorldSize calls in comparator
  const tileSize = tiles.length > 0 ? getTileWorldSize(tiles[0].z) : 0;
  const halfTile = tileSize / 2;

  const tilesWithDist = tiles.map(tile => {
    const world = tileToWorld(tile.x, tile.y, tile.z);
    const centerX = world.x + halfTile;
    const centerY = world.y + halfTile;
    const distSq = (centerX - viewportX) ** 2 + (centerY - viewportY) ** 2;
    return { tile, distSq };
  });

  tilesWithDist.sort((a, b) => a.distSq - b.distSq);

  return tilesWithDist.map(t => t.tile);
}
